import React from 'react';
import type { RunGroup } from '../../lib/paymentRunsApi';
import { paymentRunMessage } from '../../lib/paymentRunsApi';
import { formatReportMoney, supplierLabel } from '../accountsPayableReports/format';
import { paymentLabel } from './runDisplay';

/**
 * The suppliers of a run that are recorded (history: one row per payment, its reference, what it
 * paid, and a link to its voucher), or, in a run that is finished or cancelled, all of them. Not
 * editable: a recorded payment changes only by being voided from its voucher.
 */
export interface RunGroupsTableProps {
  groups: RunGroup[];
  /** Where a voucher is; the page decides how to get there. */
  onOpenVoucher: (paymentId: string) => void;
  caption: string;
}

const CELL = 'px-3 py-2 align-top';

export const RunGroupsTable: React.FC<RunGroupsTableProps> = ({ groups, onOpenVoucher, caption }) => {
  if (!groups.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="run-groups">
      <table className="data-table data-table-grid w-full min-w-[44rem] text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-slate-50">
          <tr className="text-xs text-slate-600">
            <th scope="col" className={CELL}>Supplier</th>
            <th scope="col" className={CELL}>Reference</th>
            <th scope="col" className={CELL}>Invoices</th>
            <th scope="col" className={`${CELL} text-right`}>Amount</th>
            <th scope="col" className={CELL}>Status</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.id}>
              <td className={`${CELL} font-medium`}>{supplierLabel(g.supplierName)}</td>
              <td className={CELL}>
                {g.paymentId ? (
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => onOpenVoucher(g.paymentId as string)}
                    aria-label={`Open payment voucher ${g.reference}`}
                  >
                    {g.reference}
                  </button>
                ) : (
                  g.reference
                )}
              </td>
              <td className={`${CELL} text-xs text-slate-600`}>{g.lines.map((l) => l.invoiceNumber).join(', ')}</td>
              <td className={`${CELL} text-right tabular-nums whitespace-nowrap`}>{formatReportMoney(g.amount, g.currency)}</td>
              <td className={CELL}>
                <span>{paymentLabel(g)}</span>
                {g.status === 'failed' && g.errorCode ? (
                  <div className="text-xs text-red-700">{paymentRunMessage(g.errorCode)}</div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RunGroupsTable;
